import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  InboxBlock,
  InboxBlockForeman,
  InboxEquipmentRow,
  InboxLaborRow,
  SettingsEmployee,
  SettingsEquipment,
} from '@coffman-tm/shared';
import { api, ApiCallError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { useToast } from '../ToastProvider';
import { HelpTip } from '../HelpTip';
import {
  canOfferRemember,
  rememberCheckboxLabel,
  rememberFailedToast,
  rememberToast,
} from './rememberPlaceholder';

/**
 * Compact picker for pointing a parsed labor/equipment line at an existing
 * roster entry. This is the "the foreman wrote LABORER-ID11 but it was Dave"
 * correction: reassigning the roster link makes ID/class/shift/rate flow from
 * the roster, while the parsed PDF text stays on the line as the audit of
 * what the foreman submitted. Distinct from Add-to-roster, which is for a
 * genuinely NEW person or machine.
 */
export type ReassignTarget =
  | { section: 'labor'; row: InboxLaborRow }
  | { section: 'equipment'; row: InboxEquipmentRow };

export interface InboxReassignModalProps {
  target: ReassignTarget;
  dailyEntryId: string;
  /**
   * The block's source report's foreman, when it carried a HeavyJob foreman
   * id. Non-null is what makes the Remember checkbox offerable — the id is the
   * key the decision is filed under, and the server refuses without it.
   */
  foreman?: InboxBlockForeman | null;
  onClose: () => void;
  /** Receives the rehydrated block from the reassign endpoint. */
  onReassigned: (block: InboxBlock) => void | Promise<void>;
}

const CLEAR = '__clear__';

const inputStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13, border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4, background: 'var(--surface)', color: 'var(--ink)', width: '100%',
};

interface RosterOption {
  id: string;
  label: string;
  /** Pre-lowercased haystack: name + id + description, matched as substring. */
  search: string;
}

export function InboxReassignModal({ target, dailyEntryId, foreman = null, onClose, onReassigned }: InboxReassignModalProps) {
  const { token } = useAuth();
  const toast = useToast();
  const { section } = target;
  const [employees, setEmployees] = useState<SettingsEmployee[]>([]);
  const [equipment, setEquipment] = useState<SettingsEquipment[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rosterErr, setRosterErr] = useState<string | null>(null);

  const currentId =
    section === 'labor' ? target.row.employee_id : target.row.equipment_id;
  const rawText =
    section === 'labor' ? target.row.employee_name_raw : target.row.equipment_name_raw;
  const rawId =
    section === 'labor' ? target.row.employee_hj_id_raw : target.row.asset_id_raw;

  const [choice, setChoice] = useState<string>(currentId ?? '');

  // "Remember this for {foreman}'s reports". Offered only where the server
  // would accept it (see canOfferRemember) and UNCHECKED by default: a stored
  // decision repeats itself on every later report from that foreman, so it is
  // the PM asking out loud, never a side effect of a correction.
  const canRemember =
    section === 'labor' &&
    canOfferRemember({
      foreman,
      rawId: target.row.employee_hj_id_raw,
      isUnknown: target.row.is_unknown,
      printedId: target.row.printed_id,
    });
  const [remember, setRemember] = useState(false);

  // Roster list, fetched on open — same on-demand pattern as the add-line
  // modal (the block card doesn't hold a persistent roster list). A failed
  // fetch must read as an error, not as a silently empty dropdown.
  useEffect(() => {
    if (!token) return;
    setRosterErr(null);
    const onFail = (e: unknown) =>
      setRosterErr(e instanceof Error ? e.message : 'request failed');
    if (section === 'labor') void api.settings.employees(token).then(setEmployees).catch(onFail);
    else void api.settings.equipment(token).then(setEquipment).catch(onFail);
  }, [token, section]);

  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const options = useMemo<RosterOption[]>(() => {
    if (section === 'labor') {
      return employees
        .filter((e) => e.active)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((e) => ({
          id: e.id,
          label: `${e.name} (${e.classification})`,
          search: `${e.name} ${e.heavyjob_employee_id} ${e.classification}`.toLowerCase(),
        }));
    }
    return equipment
      .filter((e) => e.active)
      .sort((a, b) => a.asset_id.localeCompare(b.asset_id) || a.description.localeCompare(b.description))
      .map((e) => ({
        id: e.id,
        label: `${e.asset_id} — ${e.description}`,
        search: `${e.asset_id} ${e.description}`.toLowerCase(),
      }));
  }, [section, employees, equipment]);

  // Type-to-filter replaces the old native <select>: the roster runs to
  // hundreds of rows, and a native dropdown gives no way to narrow it — Joey
  // was scrolling a wall of names to find one. Empty filter shows everything.
  const [filter, setFilter] = useState('');
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.search.includes(q));
  }, [options, filter]);

  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => { searchRef.current?.focus(); }, []);

  // The roster runs long, so the currently-linked entry is usually far below
  // the fold — highlighting it isn't enough on its own. Pull it into view once
  // the list first renders so "what is this line linked to right now?" is
  // answerable without scrolling. `block: 'nearest'` keeps the modal itself
  // from being scrolled by this.
  const selectedRef = useRef<HTMLButtonElement>(null);
  const didScrollToSelected = useRef(false);
  useEffect(() => {
    if (didScrollToSelected.current || !selectedRef.current) return;
    didScrollToSelected.current = true;
    selectedRef.current.scrollIntoView({ block: 'nearest' });
  }, [options]);

  const noun = section === 'labor' ? 'employee' : 'equipment';
  // Manual lines' raw text is a roster snapshot from add time, NOT foreman
  // PDF text — the copy must not claim PDF provenance for them.
  const isManual = target.row.is_manual;
  const unchanged = choice === (currentId ?? '') || (choice === CLEAR && currentId === null);
  const valid = choice !== '' && !unchanged && !busy;

  const handleConfirm = async () => {
    if (!token || !valid) return;
    setBusy(true); setErr(null);
    const targetId = choice === CLEAR ? null : choice;
    // Unlinking cannot be remembered (the server's remember_requires_target),
    // so the flag never rides along with a clear.
    const rememberThis = canRemember && remember && targetId !== null;
    try {
      if (section === 'labor') {
        const res = await api.inbox.reassignLabor(
          token,
          dailyEntryId,
          target.row.daily_labor_entry_id,
          rememberThis ? { employee_id: targetId, remember: true } : { employee_id: targetId },
        );
        // The receipt reports the two things the card cannot show: that later
        // reports will do this on their own, and how many waiting lines this
        // just filled. `remember_failed` is the other half of that promise
        // failing — the line moved, the decision did not, and saying so is the
        // difference between the PM knowing and finding out by re-typing it.
        if (res.remembered) toast.push(rememberToast(res.remembered));
        else if (res.remember_failed) toast.push(rememberFailedToast());
        await onReassigned(res.block);
      } else {
        const res = await api.inbox.reassignEquipment(
          token,
          dailyEntryId,
          target.row.daily_equipment_entry_id,
          { equipment_id: targetId },
        );
        await onReassigned(res.block);
      }
      onClose();
    } catch (e) {
      if (e instanceof ApiCallError && e.payload && typeof e.payload === 'object') {
        const p = e.payload as { error?: string; message?: string };
        setErr(p.message ?? p.error ?? 'Reassign failed');
      } else setErr(e instanceof Error ? e.message : 'Reassign failed');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => { if (!busy) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`Reassign ${noun} line`}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="confirm-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <div className="confirm-modal-title">
              Reassign {noun === 'employee' ? 'labor line' : 'equipment line'}
            </div>
            {/* The full "why this is safe" explanation lives here rather than
                as a paragraph in the body — it's read once and then in the way
                every subsequent time. The is_manual variant matters: a manual
                line's raw text is a roster snapshot from add time, NOT foreman
                PDF text, so the copy must not claim PDF provenance for it. */}
            <HelpTip title="The original entry stays put">
              {isManual
                ? 'This line was added by hand; that original entry stays on the line as the record of what was added.'
                : 'The PDF stays the record of what the foreman submitted — reassigning never rewrites it.'}{' '}
              Reassigning points the line at a roster {noun} so its rate and details bill from the
              roster.
            </HelpTip>
          </div>
        </div>
        <div className="confirm-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            {isManual ? 'Added as' : 'PDF shows'}{' '}
            <span className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              {rawText}
              {rawId && rawId !== rawText ? ` (${rawId})` : ''}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>
              {noun === 'employee' ? 'Roster employee' : 'Roster equipment'}
            </span>
            <input
              ref={searchRef}
              type="text"
              style={inputStyle}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={busy}
              placeholder={noun === 'employee' ? 'Search roster…' : 'Search assets…'}
              aria-label={`Search roster ${noun}`}
              onKeyDown={(e) => {
                // Enter commits the obvious pick — the top remaining match —
                // so a filter typed down to one result needs no mouse. It only
                // SELECTS; the Reassign button is still the commit, because
                // this writes a billing identity.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const top = filtered[0];
                  if (top) setChoice(top.id);
                }
                // Escape is handled by the modal-wide window listener; let it
                // bubble rather than stopping at the input.
              }}
            />
            {/* Always renders DOWNWARD inside the modal — no floating popover,
                so nothing can be clipped by the dialog or land offscreen. */}
            <div className="roster-picker-list" role="listbox" aria-label={`Roster ${noun}`}>
              {currentId !== null && (
                <button
                  type="button"
                  role="option"
                  aria-selected={choice === CLEAR}
                  className={`roster-picker-opt roster-picker-reset${choice === CLEAR ? ' selected' : ''}`}
                  onClick={() => setChoice(CLEAR)}
                  disabled={busy}
                >
                  {isManual ? 'Reset to original identity (unlink)' : 'Reset to PDF identity (unlink)'}
                </button>
              )}
              {filtered.map((o) => (
                <button
                  key={o.id}
                  ref={o.id === currentId ? selectedRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={choice === o.id}
                  className={`roster-picker-opt${choice === o.id ? ' selected' : ''}`}
                  onClick={() => setChoice(o.id)}
                  disabled={busy}
                >
                  {o.label}
                </button>
              ))}
              {filtered.length === 0 && !rosterErr && (
                <div className="roster-picker-empty">
                  {options.length === 0 ? 'Loading roster…' : `No match for “${filter.trim()}”`}
                </div>
              )}
            </div>
          </div>
          {rosterErr && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>
              Couldn&rsquo;t load the roster ({rosterErr}) — close this dialog and try again.
            </div>
          )}
          {choice === CLEAR && (
            <div style={{ fontSize: 12, color: 'var(--warning)' }}>
              Unlinking returns the line to its {isManual ? 'original' : 'parsed PDF'} identity —
              it will show as unknown and block Save until it&rsquo;s reassigned or added to the
              roster.
            </div>
          )}
          {/* The one place a repeated decision is made. Hidden when it cannot
              be honored (no foreman ID on the report, no ID on the line) and
              hidden on an unlink, which is not a decision about anybody.
              Unchecked by default on purpose: the portal repeats this on every
              later report from this foreman, so it has to be asked for. */}
          {canRemember && choice !== CLEAR && (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: 12.5,
                color: 'var(--ink-2)',
                lineHeight: 1.45,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={busy}
                style={{ marginTop: 2 }}
              />
              <span>
                {rememberCheckboxLabel(foreman?.name ?? null)}
                <span className="muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
                  Every later report from this foreman that prints{' '}
                  <span className="mono" style={{ fontSize: 11 }}>
                    {rawId}
                  </span>{' '}
                  fills this person in, and the line always says what the report printed. Other
                  waiting blocks with the same ID are filled now. Change it any time from here, or
                  forget it in Project Settings.
                </span>
              </span>
            </label>
          )}
          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-cta" onClick={() => void handleConfirm()} disabled={!valid}>
            {busy
              ? 'Reassigning…'
              : choice === CLEAR
                ? isManual
                  ? 'Reset line'
                  : 'Reset to PDF'
                : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}
